import { useEffect, useRef, useState, type ReactNode } from 'react';
import { instrumentSuggestionsApi, type InstrumentSuggestion } from '@/api/instrument-suggestions';
import { PasteSourceLogos } from '@/components/preview/paste/PasteSourceLogos';
import { PasteGridPreview } from '@/components/preview/PasteGridPreview';
import {
    detectClipboardSource,
    getSourceLabel,
    isTabularData,
    parseClipboardGrid,
} from '@/components/preview/test-table-editor/parseClipboardTable';
import { AppDialogLayout } from '@/components/system';
import { Button } from '@/components/ui/button';
import { primaryModifierCombo, primaryModifierKey } from '@/lib/platform';
import { capture } from '@/lib/posthog';
import { applySuggestion, buildSuggestionRequest, hasSuggestion } from './instrument-suggestion';
import { InstrumentIdentityFields, type InstrumentIdentity } from './InstrumentIdentityFields';
import { InstrumentSuggestionLine } from './InstrumentSuggestionLine';
import type { ClipboardSource } from '@/components/preview/test-table-editor/parseClipboardTable';

/** A paste already in hand, for the route that opens the dialog on its result. */
export type ConfirmedPaste = { grid: string[][]; source: ClipboardSource };

interface PasteInstrumentModalProps {
    /**
     * Skips the waiting state: the canvas route opens this dialog *because* a
     * paste already landed, so it has nothing left to wait for.
     */
    initialPaste?: ConfirmedPaste;
    /** Receives the parsed grid — first row is the header, the rest are data rows. */
    onConfirm: (grid: string[][], identity: InstrumentIdentity) => void;
    /** The caller passes `closeModal`, like `FreeInstrumentModal`'s. */
    onClose: () => void;
    /**
     * Seeds the name, for the hand-over from the catalog. Note it stays
     * invisible until a paste lands — the identity fields only render in the
     * `ready` state — so it reads as "the name was kept", never as a prompt.
     *
     * Deliberately not focused, unlike the free form's: the paste listener
     * below stands down while a text field holds the focus, so autofocusing
     * the name would swallow the very gesture this dialog waits for.
     */
    initialName?: string;
    /** Returns to whatever opened this — the catalog, with its search intact. */
    backAction?: { label: string; onClick: () => void };
    /**
     * The open laudo's patient, sent to the suggestion endpoint as an *id* so
     * the server can load the name hints its PII tripwire needs from our own
     * records. No name ever travels in that request.
     */
    patientId?: string;
}

/**
 * A key drawn at display size. The two other versions of this in the app are hints
 * at 11-12px; here the keys ARE the affordance, so they carry the weight the
 * dashed box used to — a box that promised a drop this dialog never accepted.
 */
const Keycap = ({ children }: { children: ReactNode }) => (
    <kbd className="min-w-10 rounded-lg border border-border bg-surface px-3 py-2 text-center font-sans text-base font-medium leading-none text-foreground shadow-elevation-1">
        {children}
    </kbd>
);

/** What the clipboard turned out to hold, once the user has pasted something. */
type PasteState =
    | { status: 'waiting' }
    | { status: 'rejected' }
    /**
     * `pasteSeq` counts accepted pastes. The listener stays armed in `ready`,
     * so pasting a second table leaves `status` on `'ready'` — an effect keyed
     * on the status alone would neither re-run nor abort the first request.
     */
    | ({ status: 'ready'; pasteSeq: number } & ConfirmedPaste);

/**
 * "Colar uma tabela pronta": the third way to add an instrument, for the
 * psychologist who already corrected the test in a spreadsheet.
 *
 * Pasting on the canvas has always produced the same table — this modal exists
 * because nothing said so. It names the gesture, waits for it, shows what
 * landed, and collects the two things the clipboard cannot carry: the name of
 * the instrument and its citation.
 *
 * Both routes into a pasted instrument end here — the menu item, which opens it
 * empty, and a paste on the laudo itself, which opens it with the grid already
 * in hand. That is what makes the hint under the keys true.
 */
export default function PasteInstrumentModal({
    initialPaste,
    onConfirm,
    onClose,
    initialName,
    backAction,
    patientId,
}: PasteInstrumentModalProps) {
    const [state, setState] = useState<PasteState>(
        initialPaste ? { status: 'ready', pasteSeq: 0, ...initialPaste } : { status: 'waiting' },
    );
    const [identity, setIdentity] = useState<InstrumentIdentity>({
        name: initialName ?? '',
        reference: '',
    });
    const [suggestion, setSuggestion] = useState<InstrumentSuggestion | null>(null);
    /**
     * Whether the name has been typed by hand since the last paste.
     *
     * The suggestion has no dismiss control, so this is what withdraws it: once
     * there is real work in the field, a stray click on a line that small would
     * overwrite it. A seeded `initialName` does not count — it was never typed
     * here, and replacing it with the catalog's own name is the point of the
     * hand-over from the catalog.
     */
    const [nameEdited, setNameEdited] = useState(false);

    // Aborting is not enough on its own: a request already in flight still
    // resolves after `abort()` in some paths, and its answer would describe the
    // previous table. Same guard `use-debounced-spellcheck` uses.
    const requestIdRef = useRef(0);

    // A document listener rather than a handler on the drop zone: the zone is
    // not the only focusable thing in the dialog, and a paste with the footer
    // focused has to land here just the same. `useReportPaste` stands down
    // while any dialog is open, so the two cannot both claim one paste.
    useEffect(() => {
        const handlePaste = (event: ClipboardEvent) => {
            if (event.defaultPrevented) return;

            // The name and the reference keep their own paste — same predicate
            // the canvas router uses.
            const active = document.activeElement as HTMLElement | null;
            if (
                active &&
                (active.tagName === 'INPUT' ||
                    active.tagName === 'TEXTAREA' ||
                    active.isContentEditable)
            ) {
                return;
            }

            const html = event.clipboardData?.getData('text/html') ?? '';
            const text = event.clipboardData?.getData('text/plain') ?? '';
            const parsed = parseClipboardGrid(html, text);

            event.preventDefault();

            if (!parsed || !isTabularData(parsed)) {
                setState({ status: 'rejected' });
                return;
            }

            // A new table invalidates the old proposal, including one that
            // was still on its way — and the name typed for the previous table
            // is no longer work the next proposal would be overwriting.
            setSuggestion(null);
            setNameEdited(false);
            setState((previous) => ({
                status: 'ready',
                pasteSeq: (previous.status === 'ready' ? previous.pasteSeq : 0) + 1,
                grid: parsed.grid,
                source: detectClipboardSource(html),
            }));
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, []);

    const pasteSeq = state.status === 'ready' ? state.pasteSeq : null;
    const grid = state.status === 'ready' ? state.grid : null;

    /**
     * Asks what the table is, the moment there is a table to ask about.
     *
     * Nothing on screen changes while this runs: no spinner, no skeleton, no
     * reserved space. The dialog is exactly the one that shipped before this
     * endpoint existed until an answer arrives, and it stays that way if none
     * ever does. Errors, timeouts and empty answers are all the same silence —
     * the psychologist did not ask for this, so there is nothing to apologise
     * for.
     */
    useEffect(() => {
        if (pasteSeq === null || !grid) return;

        const payload = buildSuggestionRequest(grid, {
            printedName: initialName,
            patientId,
        });
        if (!payload) return;

        const controller = new AbortController();
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        const run = async () => {
            try {
                const response = await instrumentSuggestionsApi.suggest(payload, controller.signal);
                if (requestIdRef.current !== requestId) return;

                const data = response.data;
                const useful = hasSuggestion(data);

                // Counted on the answer, not on the request: StrictMode fires
                // this effect twice in dev, and the abort makes the second one
                // the only survivor either way.
                capture('instrument_suggestion_returned', {
                    outcome: useful ? 'suggested' : 'empty',
                    suggestion_source: data.source,
                    filled_reference: Boolean(data.reference),
                });

                if (useful) setSuggestion(data);
            } catch {
                if (controller.signal.aborted || requestIdRef.current !== requestId) return;

                capture('instrument_suggestion_returned', {
                    outcome: 'failed',
                    suggestion_source: 'none',
                    filled_reference: false,
                });
            }
        };

        void run();

        return () => controller.abort();
    }, [pasteSeq, grid, initialName, patientId]);

    const handleApplySuggestion = () => {
        if (!suggestion) return;

        // Overwrites whatever is in the fields: the click is consent, and what
        // is about to land has been on screen the whole time.
        setIdentity((current) => applySuggestion(suggestion, current));
        capture('instrument_suggestion_applied', { suggestion_source: suggestion.source });
        setSuggestion(null);
    };

    /**
     * Typing the name by hand withdraws the proposal — and is the only
     * dismissal signal this design has, now that there is no X to click. It is
     * also the more honest one: the psychologist did not just wave the guess
     * away, they wrote the answer themselves.
     */
    const handleIdentityChange = (next: InstrumentIdentity) => {
        if (!nameEdited && next.name !== identity.name) {
            setNameEdited(true);
            if (suggestion) {
                capture('instrument_suggestion_dismissed', {
                    suggestion_source: suggestion.source,
                });
            }
        }

        setIdentity(next);
    };

    const handleConfirm = () => {
        if (state.status !== 'ready') return;
        onConfirm(state.grid, identity);
        onClose();
    };

    const shortcut = primaryModifierCombo('V');
    const copyShortcut = primaryModifierCombo('C');
    const modifier = primaryModifierKey();

    return (
        <AppDialogLayout
            title="Colar tabela"
            backAction={backAction}
            footer={
                <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={onClose}>
                        Cancelar
                    </Button>
                    <Button
                        type="button"
                        disabled={state.status !== 'ready'}
                        onClick={handleConfirm}
                    >
                        Colar
                    </Button>
                </div>
            }
        >
            {state.status === 'ready' ? (
                <>
                    <div className="space-y-5">
                        <PasteGridPreview parsedData={state.grid} />
                        {/* Optional, both of them: whoever pastes is after
                            speed, the name can still be fixed on the table's
                            caption, and the citation is the one thing that is
                            simply lost if it is not written here. */}
                        <InstrumentIdentityFields
                            idPrefix="paste-instrument"
                            values={identity}
                            onChange={handleIdentityChange}
                            nameFooter={
                                suggestion && !nameEdited ? (
                                    <InstrumentSuggestionLine
                                        suggestion={suggestion}
                                        onApply={handleApplySuggestion}
                                    />
                                ) : null
                            }
                        />
                    </div>
                </>
            ) : (
                <>
                    <div className="flex flex-col items-center px-6 pb-8 pt-12 text-center">
                        {/* The instruction leads into the keys, so it sits above
                            them in both states — on a rejection the sentence
                            changes, the gesture it points at does not. */}
                        {state.status === 'rejected' ? (
                            <>
                                <p className="text-base font-semibold text-destructive">
                                    O que você colou não é uma tabela
                                </p>
                                <p className="mx-auto mt-1.5 max-w-md text-sm leading-snug text-muted-foreground">
                                    Selecione as células da tabela de origem, copie com{' '}
                                    {copyShortcut} e cole de novo:
                                </p>
                            </>
                        ) : (
                            <p className="mx-auto max-w-md text-base font-semibold leading-snug">
                                Selecione as células da tabela de origem, copie com {copyShortcut} e
                                cole aqui utilizando os comandos:
                            </p>
                        )}
                        <div className="mt-5 flex items-center gap-2">
                            <Keycap>{modifier}</Keycap>
                            <span className="text-sm text-muted-foreground">+</span>
                            <Keycap>V</Keycap>
                        </div>
                        <p className="mt-7 text-xs text-muted-foreground">Funciona com</p>
                        <PasteSourceLogos className="mt-2.5" />
                    </div>
                    {/* Only shown here: the dialog that opens from a paste on
                        the laudo starts on the grid, where this would be
                        telling the user to do what they just did. */}
                    <p className="mt-4 text-center text-xs text-muted-foreground">
                        Dica: você pode colar a tabela direto no laudo com {shortcut}, sem abrir
                        esta tela.
                    </p>
                </>
            )}
        </AppDialogLayout>
    );
}
